import { config } from '@uhg-abyss/web/tools/config';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCustomQuery } from '../useCustomQuery';
import EXPORT_DIRECTORY_PDF from './DirectoryExportPdf.graphql';
import type {
  DirectoryExportPdfRequest,
  DirectoryExportPdfResponse,
  UseDirectoryExportPdfResult,
} from './types';

interface UseDirectoryExportPdfOptions {
  url?: string;
  onSuccess?: (data: DirectoryExportPdfResponse) => void;
  onError?: (error: Error) => void;
}

const getDirectoryExportPdfResponse = (
  result: any
): DirectoryExportPdfResponse | null =>
  result?.data?.exportDirectoryPdf || result?.exportDirectoryPdf || null;

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error('PDF export failed');

export const useDirectoryExportPdf = (
  options: UseDirectoryExportPdfOptions = {}
): UseDirectoryExportPdfResult => {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<DirectoryExportPdfResponse | null>(null);

  const [, mutate] = useCustomQuery(EXPORT_DIRECTORY_PDF, {
    url: options.url || config('GRAPHQL_API_URL'),
    accessor: 'exportDirectoryPdf',
    headers: {
      'Accept-Language': i18n.language || 'en',
    },
  });

  const exportPdf = async (params: DirectoryExportPdfRequest) => {
    setLoading(true);
    setError(null);

    try {
      const result = await mutate({ variables: params });
      const responseData = getDirectoryExportPdfResponse(result);

      if (!responseData) {
        throw new Error('PDF export response was empty');
      }

      if (!responseData.downloadUrl) {
        throw new Error('PDF export response did not include downloadUrl');
      }

      setData(responseData);
      options?.onSuccess?.(responseData);

      return responseData;
    } catch (err) {
      const nextError = toError(err);

      setError(nextError);
      options?.onError?.(nextError);

      throw nextError;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    data,
    exportPdf,
  };
};
